import { useQuery } from '@tanstack/react-query';
import { EmptyState, ListGroup, ListRow, Skeleton } from '@uplink/ui';
import { useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import type { Nav } from '../nav.ts';
import { usePrefs } from '../prefs.tsx';
import { FilterChips, Kpi, MarginChart, PERIOD_LABELS, type PeriodName } from './common.tsx';

const PERIOD_OPTIONS = (Object.keys(PERIOD_LABELS) as PeriodName[]).map((value) => ({
  value,
  label: PERIOD_LABELS[value],
}));

/** The admin home: money for a period, what is going on now, and the sections. */
export function AdminScreen({ nav }: { nav: Nav }) {
  const trpc = useTRPC();
  const { usd } = usePrefs();
  const [period, setPeriod] = useState<PeriodName>('30d');
  const stats = useQuery({ ...trpc.admin.stats.queryOptions({ period }), placeholderData: (prev) => prev });

  if (stats.isError) {
    return (
      <main className="ul-screen">
        <h1 className="ul-screen__title">Админка</h1>
        <EmptyState icon="shield" title="Нет доступа" text={errorText(stats.error)} />
      </main>
    );
  }

  const s = stats.data;
  const m = s?.money;
  const marginPct = m && m.revenue > 0 ? Math.round((m.net / m.revenue) * 100) : null;

  return (
    <main className="ul-screen">
      <h1 className="ul-screen__title">Админка</h1>
      <FilterChips options={PERIOD_OPTIONS} value={period} onChange={setPeriod} />

      {s && m ? (
        <div className={`ul-results ul-sections${stats.isPlaceholderData ? ' ul-results--stale' : ''}`}>
          <section className="ul-bal">
            <span className="ul-bal__label">Чистая маржа · {PERIOD_LABELS[period].toLowerCase()}</span>
            <span className={`ul-bal__amount${m.net < 0 ? ' ul-kpi__value--crit' : ''}`}>{usd(m.net)}</span>
            <span className="ul-bal__note ul-bal__note--start">
              Выручка {usd(m.revenue)}
              {marginPct !== null ? ` · маржинальность ${marginPct}%` : ''}
            </span>
            <MarginChart points={s.series.points} bucket={s.series.bucket} usd={usd} />
          </section>

          <div className="ul-kpis">
            <Kpi
              label="Выручка"
              value={usd(m.revenue)}
              sub={`домены ${usd(m.revenueDomains)} · серверы ${usd(m.revenueServers)}`}
            />
            <Kpi label="Себестоимость" value={usd(m.cost)} sub="регистратор и облако" />
            <Kpi label="Комиссии" value={usd(m.fees)} sub="CryptoBot и xRocket" />
            <Kpi label="Бонусы" value={usd(m.promo)} sub="начисления минус списания" />
            <Kpi label="Пополнения" value={usd(s.topups.amount)} sub={`${s.topups.count} платежей`} />
            <Kpi
              label="Заказы"
              value={s.orders.count}
              sub={`домены ${s.orders.domains} · серверы ${s.orders.servers}`}
            />
          </div>

          <ListGroup header="Обзор">
            <ListRow icon="wallet" title="На балансах клиентов" value={usd(s.totals.clientBalances)} />
            <ListRow
              icon="users"
              title="Пользователи"
              subtitle={`+${s.newUsers} за период`}
              value={s.totals.users}
              chevron
              onClick={() => nav.push({ name: 'adminUsers' })}
            />
            <ListRow
              icon="server"
              title="Серверы в работе"
              value={s.totals.activeServers}
              chevron
              onClick={() => nav.push({ name: 'adminServers' })}
            />
            <ListRow
              icon="globe"
              title="Домены"
              value={s.totals.activeDomains}
              chevron
              onClick={() => nav.push({ name: 'adminDomains' })}
            />
            {s.totals.needsAttention > 0 ? (
              <ListRow
                icon="alert"
                tone="crit"
                title="Заказы требуют внимания"
                value={s.totals.needsAttention}
                chevron
                onClick={() => nav.push({ name: 'adminOrders', filter: 'attention' })}
              />
            ) : null}
          </ListGroup>
        </div>
      ) : (
        <>
          <Skeleton height={260} />
          <Skeleton height={200} />
        </>
      )}

      <ListGroup header="Продажи и коммуникации">
        <ListRow
          icon="receipt"
          title="Заказы и пополнения"
          chevron
          onClick={() => nav.push({ name: 'adminOrders' })}
        />
        <ListRow
          icon="megaphone"
          title="Рассылки"
          chevron
          onClick={() => nav.push({ name: 'adminBroadcasts' })}
        />
        <ListRow icon="gift" title="Промокоды" chevron onClick={() => nav.push({ name: 'adminPromos' })} />
      </ListGroup>

      <ListGroup header="Инфраструктура">
        <ListRow
          icon="server"
          title="Каталог серверов"
          subtitle="Поставщики, ID тарифов, характеристики и цены"
          chevron
          onClick={() => nav.push({ name: 'adminServerCatalog' })}
        />
        <ListRow
          icon="key"
          title="Провайдеры"
          subtitle="Облака, домены, платежи, почта: ключи и включение"
          chevron
          onClick={() => nav.push({ name: 'adminProviders' })}
        />
        <ListRow
          icon="shield"
          title="Настройки VPN"
          subtitle="Узлы для подписок и пробных ключей"
          chevron
          onClick={() => nav.push({ name: 'adminVpnSettings' })}
        />
      </ListGroup>

      <ListGroup header="Мониторинг">
        <ListRow
          icon="alert"
          title="Журнал ошибок"
          subtitle="Серверы, VPN, домены, платежи и фоновые задания"
          chevron
          onClick={() => nav.push({ name: 'adminSystemErrors' })}
        />
        <ListRow
          icon="chart"
          title="Проверка VPN в России"
          subtitle="Публичный статус, статистика и история"
          chevron
          onClick={() => nav.push({ name: 'adminVpnStatus' })}
        />
      </ListGroup>
    </main>
  );
}
